/**
 * quoteUi.jsx - the small form pieces the quote builder is made of: a
 * labelled field, inputs, a section card and buttons, in the app's look
 * (Montserrat, deep green, the opportunity editor's pale panels).
 */
import { signedMoney, upTo2 } from '../../utils/format';
export const GREEN = '#1B5E20';
export const LINE = '#E5E9DD';
export const PANEL = '#FAFBF7';
export const MUTED = '#6B7280';

export const inputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '6px 8px',
  borderRadius: 6,
  border: `1px solid ${LINE}`,
  fontSize: 12,
  fontFamily: "'Montserrat', 'Inter', sans-serif",
  color: '#1F2328',
  background: '#fff',
  minWidth: 0,
};

export function Section({ title, hint, right, children }) {
  return (
    <section style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 10, padding: '14px 16px', marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, marginBottom: hint ? 4 : 10 }}>
        <h3 style={{ margin: 0, fontSize: 12, fontWeight: 800, color: GREEN, letterSpacing: 0.6, textTransform: 'uppercase' }}>{title}</h3>
        {right}
      </div>
      {hint && <div style={{ fontSize: 11, color: MUTED, marginBottom: 10 }}>{hint}</div>}
      {children}
    </section>
  );
}

export function Field({ label, children, span = 1, title }) {
  return (
    <label title={title} style={{ display: 'flex', flexDirection: 'column', gap: 3, gridColumn: `span ${span}`, minWidth: 0 }}>
      <span style={{ fontSize: 10, fontWeight: 700, color: MUTED, letterSpacing: 0.4, textTransform: 'uppercase' }}>{label}</span>
      {children}
    </label>
  );
}

export function TextInput({ value, onChange, disabled, placeholder, type = 'text', style, ...rest }) {
  return (
    <input
      type={type}
      value={value ?? ''}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      style={{ ...inputStyle, ...(disabled ? { background: '#F4F5F2' } : null), ...style }}
      {...rest}
    />
  );
}

/** A number box that keeps what is typed (so "1." can become "1.5") and hands back the text. */
export function NumInput({ value, onChange, disabled, placeholder, style, ...rest }) {
  return (
    <input
      inputMode="decimal"
      value={value ?? ''}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value.replace(/[^0-9.]/g, ''))}
      style={{ ...inputStyle, textAlign: 'right', ...(disabled ? { background: '#F4F5F2' } : null), ...style }}
      {...rest}
    />
  );
}

export function SelectInput({ value, onChange, options, disabled, blank, style, ...rest }) {
  const opts = (options || []).map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
  const known = opts.some((o) => o.value === (value ?? ''));
  return (
    <select
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      style={{ ...inputStyle, ...style }}
      {...rest}
    >
      {blank !== undefined && <option value="">{blank}</option>}
      {!known && value ? <option value={value}>{value}</option> : null}
      {opts.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export function Grid({ cols = 4, children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: 10 }}>{children}</div>;
}

export function Btn({ children, onClick, primary, danger, disabled, title, small, style, ...rest }) {
  const base = {
    padding: small ? '4px 10px' : '8px 14px',
    borderRadius: 6,
    fontSize: small ? 10.5 : 11,
    fontWeight: 700,
    fontFamily: "'Montserrat', 'Inter', sans-serif",
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.55 : 1,
    whiteSpace: 'nowrap',
    border: `1px solid ${primary ? GREEN : danger ? '#FFCDD2' : '#D6DCCB'}`,
    background: primary ? GREEN : '#fff',
    color: primary ? '#fff' : danger ? '#B71C1C' : '#1F2328',
  };
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title} style={{ ...base, ...style }} {...rest}>
      {children}
    </button>
  );
}

/** A small table head cell / body cell for the builder's line tables. */
export const th = {
  textAlign: 'left',
  fontSize: 10,
  fontWeight: 700,
  color: MUTED,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  padding: '4px 6px',
  borderBottom: `1px solid ${LINE}`,
};
export const td = { padding: '3px 4px', verticalAlign: 'middle', borderBottom: '1px solid #F1F3EE' };

const USD = { code: 'USD', symbol: '$', label: 'United States Dollars' };

/** The rate card's currency { code, symbol, label } (USD when it names none), as the server's quote engine reads it. */
export function currencyOf(settings) {
  const c = (settings && settings.currency) || {};
  const code = String(c.code || '')
    .trim()
    .toUpperCase();
  if (!code || code === 'USD') return USD;
  return { code, symbol: String(c.symbol || '').trim() || code, label: String(c.label || '').trim() || code };
}

/** "Saudi Riyals (SAR)" - which currency a card or builder is in. */
export const currencyName = (cur) => `${cur.label} (${cur.code})`;

/**
 * $1,234.50, or in the card's currency (a symbol or { symbol }): a symbol
 * written in letters gets a space, "SAR 1,234.50" - as the documents print it.
 */
export const fmtMoney = (v, cur) => {
  const sym = typeof cur === 'string' ? cur : (cur && cur.symbol) || '$';
  const n = Math.round((Number(v) || 0) * 100) / 100;
  return signedMoney(n, /[A-Za-z]$/.test(sym) ? `${sym} ` : sym);
};
export const fmtNum = (v) => {
  const n = Number(v) || 0;
  return Number.isInteger(n) ? n.toLocaleString('en-US') : upTo2(n);
};
